"use client"

import { useLocaleFromPathname } from "@/app/(site)/_utils/_hooks/useLocaleFromPathname"
import { resolveApp, type AppConfig } from "@/i18n/apps"

/**
 * Client-side: resolve the `AppConfig` for the current URL locale.
 *
 * This is the canonical pattern for Bucket A consumers (per `PLAN.RemoveCentraFromProxy.md`
 * Phase 2): values that depend only on the URL locale (market, pricelist, language,
 * country code, currency, hrefLang) should derive from `useAppForCurrentLocale()`,
 * not from the Zustand `basketSession` store. This means the values are correct
 * for browsing visitors who have no Centra session.
 *
 * Notes per `vercel-react-best-practices`:
 * - **§5.3**: no `useMemo` here. `resolveApp` is a `Map.get` (one or two probes); the
 *   hook overhead of `useMemo` would dominate the saved work. The result is also
 *   referentially stable for the same locale (same `Map` value), so consuming
 *   components that read `app.market` won't re-render unnecessarily.
 * - **§5.10**: only the locale string is subscribed via `useLocaleFromPathname`
 *   (which subscribes to `usePathname`). Re-renders fire on navigation, which is
 *   the correct cadence.
 * - **§5.4**: components consuming `app.X` should pass primitive props down rather
 *   than passing the whole `app` object — see Phase 2 migration prose.
 */
export const useAppForCurrentLocale = (): AppConfig => {
    const locale = useLocaleFromPathname()
    return resolveApp(locale)
}
